// Polyfill for Promise.allSettled, which may be missing in older webviews.
if (typeof Promise.allSettled !== "function") {
  Promise.allSettled = function (promises) {
    const wrappedPromises = promises.map((p) =>
      Promise.resolve(p).then(
        (value) => ({ status: "fulfilled", value }),
        (reason) => ({ status: "rejected", reason })
      )
    );
    return Promise.all(wrappedPromises);
  };
}

/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import App from "./components/App";
import { FluentProvider } from "@fluentui/react-components";
import * as React from "react";
import * as ReactDOM from "react-dom";
import { sourcestoneLightTheme } from "./theme";
/* global document, Office, module, require */

import { applyUnsupportedWebviewBanner } from "./unsupportedWebview";

// MSAL imports
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { loginRequest, msalConfig } from "../authConfig";

const msalInstance = new PublicClientApplication(msalConfig);
console.log("typeof Office:", typeof Office);

// Apply this before any render so CSP can remain strict (no inline scripts).
applyUnsupportedWebviewBanner();

// Silence the Chromium ResizeObserver warning that React dev overlay treats as fatal.
const isResizeObserverLoopError = (value: unknown): boolean => {
  return typeof value === "string" && value.includes("ResizeObserver loop completed");
};

window.addEventListener(
  "error",
  (event) => {
    if (isResizeObserverLoopError(event.message)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  },
  { capture: true }
);

const previousOnError = window.onerror;
window.onerror = function (message, source, lineno, colno, error) {
  if (isResizeObserverLoopError(message) || (error && isResizeObserverLoopError(error?.message))) {
    return true; // swallow so React overlay does not trigger
  }
  if (typeof previousOnError === "function") {
    return previousOnError.call(this, message, source, lineno, colno, error);
  }
  return false;
};

const originalConsoleError = window.console.error.bind(window.console);
window.console.error = (...args: unknown[]) => {
  if (args.length > 0 && isResizeObserverLoopError(args[0])) {
    return;
  }
  originalConsoleError(...args);
};

// CSP diagnostics (local verification helper)
// We keep this lightweight so you can prove CSP is active in the current webview
// and that you are seeing violations in DevTools before enforcing the policy.
window.addEventListener("securitypolicyviolation", (event: SecurityPolicyViolationEvent) => {
  // Use console.error so it remains visible even if DevTools is filtered to "Errors only".
  const directive = event.effectiveDirective || "";
  const blocked = event.blockedURI || "";
  const source = event.sourceFile || "";
  console.error(`[CSP violation] directive=${directive} blocked=${blocked} source=${source}`);
});

// Sanity beacon: confirms this bundle version is actually running in the task pane.
console.error("[CSP] taskpane bundle loaded; CSP listener installed");

// CSP reporting probe (temporary) - DISABLED:
// We previously exposed SS_triggerCspProbe() which intentionally triggered an img-src CSP violation.
// Disabled so it doesn't generate noise during normal usage.
//
// To re-enable, uncomment the block below.
//
// try {
//   const triggerCspProbe = () => {
//     const probe = document.createElement("img");
//     probe.alt = "SS CSP probe";
//     probe.style.display = "none";
//     probe.src = "https://example.com/ss-csp-probe.png";
//     document.body?.appendChild(probe);
//   };
//   (window as any).SS_triggerCspProbe = triggerCspProbe;
// } catch {
//   // Never allow diagnostics to break the task pane.
// }

// Force a light-touch re-render when ResizeObserver hits multiple layout changes in the same frame.
const originalResizeObserver = window.ResizeObserver;

if (originalResizeObserver) {
  const SafeResizeObserver: typeof originalResizeObserver = function (
    this: ResizeObserver,
    callback: ResizeObserverCallback
  ) {
    const safeCallback: ResizeObserverCallback = (entries, observer) => {
      try {
        callback(entries, observer);
      } catch (error) {
        console.error("ResizeObserver callback failed", error);
        window.requestAnimationFrame(() => {
          document.body?.getBoundingClientRect();
        });
      }
    };

    return new originalResizeObserver(safeCallback);
  } as unknown as typeof originalResizeObserver;

  SafeResizeObserver.prototype = originalResizeObserver.prototype;
  window.ResizeObserver = SafeResizeObserver;
}

const renderTaskPane = (): void => {
  const rootElement = document.getElementById("container");
  ReactDOM.render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <FluentProvider theme={sourcestoneLightTheme}>
          <App />
        </FluentProvider>
      </MsalProvider>
    </React.StrictMode>,
    rootElement
  );
};

const queryParameters = new URLSearchParams(window.location.search);
const isAdminConsentRedirect = queryParameters.get("action") === "admin-consent";
const isAdminConsentCallback =
  queryParameters.get("state") === "admin-storage-setup" &&
  (queryParameters.has("admin_consent") || queryParameters.has("error"));

if (isAdminConsentRedirect) {
  const backendScope = loginRequest.scopes[0] || "";
  const backendClientId = backendScope.match(/^api:\/\/([^/]+)\/.+$/i)?.[1];
  if (!backendClientId) {
    throw new Error("The backend application identifier is not configured.");
  }
  const consentUrl = new URL("https://login.microsoftonline.com/organizations/adminconsent");
  consentUrl.searchParams.set("client_id", backendClientId);
  consentUrl.searchParams.set(
    "redirect_uri",
    `${window.location.origin}${window.location.pathname}`
  );
  consentUrl.searchParams.set("state", "admin-storage-setup");
  window.location.assign(consentUrl.toString());
} else if (isAdminConsentCallback) {
  Office.onReady(() => {
    Office.context.ui.messageParent(
      JSON.stringify({
        type: "sourcestone-admin-consent-complete",
        success:
          queryParameters.get("admin_consent")?.toLowerCase() === "true" &&
          !queryParameters.has("error"),
        error: queryParameters.get("error") || "",
      })
    );
    window.close();
  });
} else {
  Office.onReady(renderTaskPane);
}

if ((module as any).hot) {
  (module as any).hot.accept("./components/App", () => {
    const NextApp = require("./components/App").default;
    // This part of the code is not directly related to the new_code,
    // so it's kept as is.
    const rootElement = document.getElementById("container");
    ReactDOM.render(
      <React.StrictMode>
        <MsalProvider instance={msalInstance}>
          <FluentProvider theme={sourcestoneLightTheme}>
            <NextApp />
          </FluentProvider>
        </MsalProvider>
      </React.StrictMode>,
      rootElement
    );
  });
}
